import React from "react";
import { FormatListBulleted, ViewColumn } from "@mui/icons-material";
import { kanbanColumns, workListRows } from "../../../../data/workspaceData";
import { WorkKanbanView } from "../../common/WorkKanbanView";
import { SectionHeader } from "../../common/WorkspaceChrome";

const documentationLaneAccent = {
  backlog: "#94a3b8",
  todo: "#2563eb",
  in_progress: "#f97316",
  review: "#22c55e",
  blocked: "#dc2626",
  done: "#16a34a",
};

const getLaneKey = (status = "") => `${status}`.trim().toLowerCase().replace(/\s+/g, "_");

const getTypeMarkerKind = (type = "") => {
  const normalizedType = `${type}`.toLowerCase();
  if (normalizedType.includes("bug")) return "bug";
  if (normalizedType.includes("story")) return "story";
  if (normalizedType.includes("support") || normalizedType.includes("sales")) return "support";
  if (normalizedType.includes("incident")) return "incident";
  if (normalizedType.includes("improvement")) return "improvement";
  if (normalizedType.includes("feature")) return "feature";
  if (normalizedType.includes("task") || normalizedType.includes("test") || normalizedType.includes("hr")) return "task";
  return "default";
};

function DocumentationWorkTypeMark({ type }) {
  const kind = getTypeMarkerKind(type);
  const iconByKind = {
    bug: <><path d="M9 8.5V6.8a3 3 0 0 1 6 0v1.7" /><rect height="10" rx="4" width="9" x="7.5" y="8.5" /><path d="M4.5 10.5h3M4.5 15.5h3m9 0h3m-3-5h3M12 8.5V4" /></>,
    story: <><path d="M5 4.5h6.2A2.8 2.8 0 0 1 14 7.3V20H7a2 2 0 0 0-2 2V4.5Z" /><path d="M19 4.5h-6.2A2.8 2.8 0 0 0 10 7.3V20h7a2 2 0 0 1 2 2V4.5Z" /></>,
    support: <><circle cx="12" cy="12" r="8.5" /><path d="M8.4 9.4a3.7 3.7 0 0 1 7.1 1.4c0 2.7-3.5 2.5-3.5 4.8M12 18h.01" /></>,
    task: <><rect height="17" rx="2" width="17" x="3.5" y="3.5" /><path d="m7.4 12 3 3 6.3-6.2" /></>,
  };
  return <i aria-hidden="true" className={`work-type-mark is-${kind}`}><svg fill="none" viewBox="0 0 24 24">{iconByKind[kind] || <><rect height="15" rx="2" width="15" x="4.5" y="4.5" /><path d="M8.5 12h7" /></>}</svg></i>;
}

const documentationBoardLanes = kanbanColumns.map((column) => {
  const key = getLaneKey(column.status);
  return {
    key,
    label: column.status,
    records: column.items.map((item) => ({
      ...item,
      id: item.key,
      due: item.due || (column.status === "Done" ? "Done" : column.status === "Blocked" ? "Needs attention" : "Upcoming"),
      owner: item.owner || "Workspace team",
      status: column.status,
    })),
  };
});

export function WorkBoard({ workView, setWorkView }) {
  return (
    <section className="panel documentation-work-views-panel">
      <div className="view-header">
        <SectionHeader
          eyebrow="Work Views"
          title="List and Kanban for every team workflow"
          text="Track tasks, bugs, tests, support tickets, HR items, stories, epics, and sales follow-ups."
        />
        <div className="segmented-control" aria-label="Work view">
          {["List", "Kanban"].map((view) => (
            <button
              className={workView === view ? "active" : ""}
              key={view}
              onClick={() => setWorkView(view)}
              type="button"
            >
              {view === "List" ? <FormatListBulleted fontSize="small" /> : <ViewColumn fontSize="small" />}
              <span>{view}</span>
            </button>
          ))}
        </div>
      </div>

      {workView === "List" ? (
        <div className="work-list" role="table" aria-label="Workspace work list">
          <div className="work-list-head" role="row">
            <span>Key</span>
            <span>Title</span>
            <span>Type</span>
            <span>Owner</span>
            <span>Priority</span>
            <span>Status</span>
            <span>Due</span>
            <span>Sprint</span>
          </div>
          {workListRows.map((row) => (
            <div className="work-list-row" key={row.key} role="row">
              <strong>{row.key}</strong>
              <p>{row.title}</p>
              <span>{row.type}</span>
              <span>{row.owner}</span>
              <em className={`priority ${row.priority.toLowerCase()}`}>{row.priority}</em>
              <span>{row.status}</span>
              <span>{row.due}</span>
              <small>{row.sprint}</small>
            </div>
          ))}
        </div>
      ) : (
        <WorkKanbanView
          ariaLabel="Workspace Kanban board"
          getDateLabel={(value) => value || "Upcoming"}
          laneAccentMap={documentationLaneAccent}
          lanes={documentationBoardLanes}
          onOpenRecord={() => {}}
          renderTypeMark={(type) => <DocumentationWorkTypeMark type={type} />}
        />
      )}
    </section>
  );
}
